import type Keycloak from 'keycloak-js';
import type { KeycloakConfig } from 'keycloak-js';
import { createKeycloakFromEnv } from './keycloak-factory';
import { createRouteGuards } from '../guards/route-guards';
import { useAuth } from '../hooks/use-auth';

/**
 * Complete auth setup result containing Keycloak instance and route guards
 */
export interface AuthGuardsSetup {
  /** Configured Keycloak instance */
  keycloak: Keycloak;
  /** Authentication guard function */
  requireAuthentication: () => void;
  /** Role-based authorization guard factory */
  requireRoles: (roles: string[]) => () => void;
  /** useAuth hook for component-level authentication */
  useAuth: typeof useAuth;
}

/**
 * Singleton manager for auth guards instances
 */
class AuthGuardsSingleton {
  private static instances = new Map<string, AuthGuardsSetup>();

  /**
   * Creates a unique key for the configuration
   */
  private static createConfigKey(config: KeycloakConfig): string {
    return `${config.url}:${config.realm}:${config.clientId}`;
  }

  /**
   * Gets or creates an auth guards instance with singleton pattern
   */
  static getInstance(config: KeycloakConfig): AuthGuardsSetup {
    const configKey = this.createConfigKey(config);
    
    // Return existing instance if available
    if (this.instances.has(configKey)) {
      return this.instances.get(configKey)!;
    }

    // Create Keycloak instance
    const keycloak = createKeycloakFromEnv(config);
    
    // Create route guards with the Keycloak instance
    const guards = createRouteGuards(keycloak);
    
    const instance: AuthGuardsSetup = {
      keycloak,
      ...guards,
      useAuth,
    };
    
    // Store the instance
    this.instances.set(configKey, instance);
    
    return instance;
  }

  /**
   * Resets a specific instance or all instances
   */
  static reset(config?: KeycloakConfig): void {
    if (config) {
      const configKey = this.createConfigKey(config);
      this.instances.delete(configKey);
    } else {
      this.instances.clear();
    }
  }
}

/**
 * Creates auth guards from configuration with singleton pattern.
 * 
 * @param config - Keycloak configuration object
 * @returns Object containing the Keycloak instance and configured route guards
 */
export function createAuthGuards(config: KeycloakConfig): AuthGuardsSetup {
  return AuthGuardsSingleton.getInstance(config);
}

// Type exports
export type AuthGuardsInstance = AuthGuardsSetup;